/**
 * Maps Medplum Practitioner / Patient resources (RBAC pilot–tagged) into
 * the Users section `User` model for Settings → Users / Practices & Users.
 */

import { formatHumanName } from '@medplum/core';
import {
  formatPractitionerNameParts,
  getPractitionerOrganizationRef,
} from '@unify/fhir-model/resources/practitioner';
import type { MedplumClient, ProfileResource } from '@medplum/core';
import type { Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { ALIASES_EXT, BIO_EXT, CLINICAL_EXT } from '@unify/fhir-model/constants';
import { EXTENSION_URLS } from '../features/rbac/constants';
import { directoryPracticeLabel } from './adminDirectoryMapping';
import type { Role, User } from '../pages/users/types/rbac';

function extensionValueString(
  resource: { extension?: { url?: string; valueString?: string }[] },
  url: string,
): string | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueString;
}

function profileFields(
  resource: Practitioner | Patient,
): Pick<User, 'bio' | 'aliases' | 'clinical'> {
  const bio = extensionValueString(resource, BIO_EXT);
  let aliases: string[] | undefined;
  let clinical: User['clinical'] | undefined;
  try {
    const raw = extensionValueString(resource, ALIASES_EXT);
    aliases = raw ? JSON.parse(raw) as string[] : undefined;
  } catch {
    aliases = undefined;
  }
  try {
    const raw = extensionValueString(resource, CLINICAL_EXT);
    clinical = raw ? JSON.parse(raw) as User['clinical'] : undefined;
  } catch {
    clinical = undefined;
  }
  return {
    ...(bio ? { bio } : {}),
    ...(aliases?.length ? { aliases } : {}),
    ...(clinical && Object.keys(clinical).length ? { clinical } : {}),
  };
}

function parseRole(value: string | undefined, fallback: Role): Role {
  const allowed: Role[] = [
    'unify-admin',
    'practice-owner',
    'practice-admin',
    'supervisor',
    'provider',
    'client',
  ];
  if (value && (allowed as string[]).includes(value)) {
    return value as Role;
  }
  return fallback;
}

function extensionRole(
  resource: { extension?: { url?: string; valueString?: string }[] },
): string | undefined {
  return resource.extension?.find((e) => e.url === EXTENSION_URLS.role)?.valueString;
}

function formatMetaDate(iso: string | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export function practitionerToDirectoryUser(p: Practitioner): User | null {
  if (!p.id) return null;
  const role = parseRole(extensionRole(p), 'provider');
  const parts = formatPractitionerNameParts(p);
  const name = parts.primary || 'Unknown';
  const email = p.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const lastUp = p.meta?.lastUpdated;
  const orgRef = getPractitionerOrganizationRef(p);
  const orgId = orgRef?.reference?.replace(/^Organization\//, '') ?? '';
  const labeled = directoryPracticeLabel({
    role,
    organizationId: orgId,
    organizationName: orgRef?.display,
  });

  return {
    id: p.id,
    name,
    ...(parts.legal ? { legalName: parts.legal } : {}),
    email,
    phone: p.telecom?.find((t) => t.system === 'phone')?.value,
    role,
    practiceId: labeled.practiceId,
    practiceName: labeled.practiceName,
    status: p.active === false ? 'Inactive' : 'Active',
    createdAt: formatMetaDate(lastUp),
    lastActive: lastUp?.trim() ? lastUp : '—',
    ...profileFields(p),
  };
}

export function patientToDirectoryUser(p: Patient): User | null {
  if (!p.id) return null;
  const role = parseRole(extensionRole(p), 'client');
  const name =
    p.name?.[0]?.text?.trim() ||
    formatHumanName(p.name?.[0]) ||
    'Unknown';
  const email = p.telecom?.find((t) => t.system === 'email')?.value ?? '';
  const lastUp = p.meta?.lastUpdated;
  return {
    id: p.id,
    name,
    email,
    phone: p.telecom?.find((t) => t.system === 'phone')?.value,
    role,
    practiceId: '',
    practiceName: '',
    status: p.active === false ? 'Inactive' : 'Active',
    createdAt: formatMetaDate(lastUp),
    lastActive: lastUp?.trim() ? lastUp : '—',
    ...profileFields(p),
  };
}

/**
 * Resolve the logged-in user row for Settings. Prefer directory search by id/email,
 * then derive from the session profile so we never fall back to an arbitrary list row
 * (directory is sorted by name; `users[0]` was the wrong user for many accounts).
 */
export function resolveDirectoryUserFromProfile(
  profile: ProfileResource | undefined,
  users: User[],
): User | null {
  if (!profile?.id) {
    return null;
  }
  const profileEmail = profile.telecom?.find((t) => t.system === 'email')?.value?.trim().toLowerCase();

  const byId = users.find((u) => u.id === profile.id);
  if (byId) {
    return byId;
  }
  if (profileEmail) {
    const byEmail = users.find((u) => u.email?.trim().toLowerCase() === profileEmail);
    if (byEmail) {
      return byEmail;
    }
  }
  if (profile.resourceType === 'Practitioner') {
    return practitionerToDirectoryUser(profile as Practitioner);
  }
  if (profile.resourceType === 'Patient') {
    return patientToDirectoryUser(profile as Patient);
  }
  return null;
}

/**
 * Session profile (`useMedplumProfile` / `getProfile`) is not updated by
 * `updateResource`. Self-service profile reads must bypass the in-memory cache.
 */
export const PROFILE_RESOURCE_READ_OPTIONS = { cache: 'no-cache' } as const;

export async function loadFreshDirectoryUserFromProfile(
  medplum: Pick<MedplumClient, 'readResource'>,
  resourceType: 'Practitioner' | 'Patient',
  id: string,
): Promise<User | null> {
  const fresh = await medplum.readResource(resourceType, id, PROFILE_RESOURCE_READ_OPTIONS);
  const user = resolveDirectoryUserFromProfile(fresh, []);
  if (!user || fresh.resourceType !== 'Practitioner') {
    return user;
  }
  const orgRef = getPractitionerOrganizationRef(fresh);
  const orgId = orgRef?.reference?.replace(/^Organization\//, '');
  if (!orgId) {
    return user;
  }
  try {
    const org = await medplum.readResource('Organization', orgId, PROFILE_RESOURCE_READ_OPTIONS) as Organization;
    const labeled = directoryPracticeLabel({
      role: user.role,
      organizationId: org.id ?? orgId,
      organizationName: org.name ?? orgRef?.display,
    });
    return { ...user, practiceId: labeled.practiceId, practiceName: labeled.practiceName };
  } catch {
    return user;
  }
}
